/**
 * RulesSection Component
 * Displays and manages a single rule for a property or unit.
 * Rules are auto-created with entities; this component only supports viewing and editing.
 */

import { useState, useEffect, useCallback } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { MarkdownThemeScope } from '../MarkdownThemeScope';
import { rulesApi, type RuleResponse } from '../../lib/api';
import { PageSection } from '../layout/index';
import { Button } from '../ui/button';
import './md-editor-overrides.css';

interface RulesSectionProps {
  scope: 'PROPERTY' | 'UNIT';
  propertyId?: string;
  unitId?: string;
}

export function RulesSection({ scope, propertyId, unitId }: RulesSectionProps) {
  const [rules, setRules] = useState<RuleResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let result: { rules: RuleResponse[] };
      if (scope === 'PROPERTY' && propertyId) {
        result = await rulesApi.listForProperty(propertyId);
      } else if (scope === 'UNIT' && unitId) {
        result = await rulesApi.listForUnit(unitId);
      } else {
        setRules([]);
        return;
      }
      setRules(result.rules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [scope, propertyId, unitId]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const rule = rules[0] ?? null;

  const handleEdit = () => {
    if (rule) {
      setEditContent(rule.content);
      setEditing(true);
      setSaveError(null);
    }
  };

  const handleSave = async () => {
    if (!rule) return;
    setSaving(true);
    setSaveError(null);
    try {
      await rulesApi.update(rule.id, { content: editContent });
      setEditing(false);
      await loadRules();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setSaveError(null);
  };

  return (
    <PageSection
      title="Rules & Regulations"
      action={
        !editing && rule ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEdit}
            data-testid="edit-rule-button"
          >
            Edit
          </Button>
        ) : undefined
      }
      data-testid="rules-section"
    >
      <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
        {error && (
          <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Loading rules...
          </div>
        ) : !rule ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p className="m-0">No rules available.</p>
          </div>
        ) : editing ? (
          <div data-testid="rule-item">
            {saveError && (
              <div className="p-3 mb-3 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md">
                {saveError}
              </div>
            )}
            <MarkdownThemeScope className="mb-3" data-testid="rule-content-input">
              <MDEditor
                value={editContent}
                onChange={(val) => setEditContent(val || '')}
                height={300}
                preview="edit"
              />
            </MarkdownThemeScope>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-rule-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div data-testid="rule-item">
            <MarkdownThemeScope>
              <MDEditor.Markdown source={rule.content} />
            </MarkdownThemeScope>
          </div>
        )}
      </div>
    </PageSection>
  );
}

export default RulesSection;
